/** Owner-column cell: the item's assignment group rendered as a link to the
 *  Preflight User Groups page, with a hover popover listing members. Falls back to
 *  the legacy per-user owner / owner_role text when the item has no assigned group.
 *  Shared by the per-TO Preflight tab and the system-wide Items grid.
 *
 *  The members in the tooltip are links: "who do I chase about this item" is the
 *  whole reason the column exists, and the names used to be dead text. MUI
 *  tooltips are interactive by default, so the pointer can travel into the
 *  panel; each link stops propagation so a grid row click is not triggered on
 *  the way. */
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { PreflightItem } from '@/api/preflight';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { userActions } from '@/features/tour-operators/tenantLinks';

export function AssignedGroupLink({ item }: { item: PreflightItem }) {
  const g = item.assigned_group;

  if (!g) {
    if (item.owner) {
      return (
        <Typography variant="caption" color="text.secondary" noWrap>
          <ValueActions actions={userActions(item.owner)}>
            {item.owner.name || item.owner.email}
          </ValueActions>
        </Typography>
      );
    }
    return (
      <Typography variant="caption" color="text.secondary" noWrap>
        {item.owner_role || '—'}
      </Typography>
    );
  }

  const extra = g.member_count - g.members.length;
  const tip = (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
        {g.name} · {g.member_count} member{g.member_count === 1 ? '' : 's'}
      </Typography>
      {g.members.length === 0 ? (
        <Typography variant="caption">No members yet</Typography>
      ) : (
        g.members.map((m) => (
          <Typography key={m.id} variant="caption" sx={{ display: 'block' }}>
            <Link
              component={RouterLink}
              to={`/users/${m.id}`}
              color="inherit"
              underline="hover"
              onClick={(e) => e.stopPropagation()}
            >
              {m.name || m.email}
            </Link>
          </Typography>
        ))
      )}
      {extra > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          +{extra} more
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tip} arrow>
      <Link
        component={RouterLink}
        to="/preflight-user-groups"
        variant="caption"
        underline="hover"
        onClick={(e) => e.stopPropagation()}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {g.name}
        {g.member_count > 0 ? ` (${g.member_count})` : ''}
      </Link>
    </Tooltip>
  );
}
